import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'material-ui/styles';
import { Doughnut } from 'react-chartjs-2';
import Loader from "../../Parts/Loader";
import 'chartjs-plugin-datalabels';
import 'chartjs-plugin-doughnutlabel';
import PanelHeader from '../parts/PanelHeader';
import { TimelineLabelType, DashboardTimelineType, DateRanges, OwnerType } from '../../../constants/ComponentTypes';
import { addCommas } from '../../../utils/utils';
import { hasKLFollowCallBeenEnabled } from '../../../utils/userUtils';
import { connect } from 'react-redux';
import * as actions from '../../../redux/actions/OrderDashboard';
import { getCurrentPersonAllStatus } from '../../../redux/actions/CurrentPersonStatus';

const styles = () => ({
  root: {
    width: '100%',
    padding: '22px 25px 32px 25px',
    background: '#FFFFFF',
    boxShadow: '0px 1px 2px #0000001F',
    borderRadius: '4px',
    boxSizing: 'border-box',
    marginBottom: '24px',
  },

  graphContainer: {
    display: 'flex',
    justifyContent: 'center',
    position: 'relative',
    width: '100%',
  },

  graphInfo: {
    marginLeft: 50,
    fontSize: 13,
  },

  circle: {
    width: 15,
    height: 15,
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: 5,
    verticalAlign: 'text-top',
  },

  loaderContainer: {
    height: "100px",
    position: "relative",
    width: "100%",
  },

  emptyContainer: {
    width: '100%',
    padding: '15px 0px',
    fontSize: '13px',
  },

  info: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 30,
    fontWeight: 'bold',
    fontSize: 14,
  },

  infoContainer: {
    display: 'flex',
    alignItems: 'center',
  },

  statusText: {
    width: 115,
  },

  infoText: {
    width: 55,
    marginLeft: 5,
    textAlign: 'end',
  },

  link: {
    color: '#181819',
    textDecoration: 'none',
    cursor: 'pointer'
  }
});

class StatusChartPanel extends React.Component {
  state = {
    dateSelected: DateRanges.PAST_1_YEAR,
    filterValue: {
      created_at_from: DateRanges.PAST_1_YEAR.from,
      created_at_to: DateRanges.PAST_1_YEAR.to,
    }
  }

  componentDidMount() {
    let query = this.filterValueToQuery();
    !this.props.isDemo && this.props.getCurrentPersonAllStatus({
      ...query
    }, true);
    !this.props.isDemo && this.props.getOrdersOverview({
      ...query
    }, true);
  }

  componentDidUpdate(prevProps) {
    // 上位コンポーネントのステートによって。初期クエリ化のクエリを実行する
    if (prevProps.ownerType && prevProps.ownerType !== this.props.ownerType) {
      this.setState({
        dateSelected: DateRanges.PAST_1_YEAR,
        filterValue: { ...this.state.filterValue, ...{ 'created_at_from': DateRanges.PAST_1_YEAR.from, 'created_at_to': DateRanges.PAST_1_YEAR.to } },
      }, () => {
        let query = this.filterValueToQuery();
        !this.props.isDemo && this.props.getCurrentPersonAllStatus({
          ...query
        }, true);
        !this.props.isDemo && this.props.getOrdersOverview({
          ...query
        }, true);
      });
    }
  }

  calculatePercentage(number, sum) {
    if (number === 0) {
      return 0;
    }
    const denominator = 100;
    const percentage = (number / sum) * denominator;
    // 小数点1桁に四捨五入して表示
    const timesForInt = 10;
    return Math.round(percentage * timesForInt) / timesForInt;
  }

  renderLink = (status, info) => {
    const { classes } = this.props;
    return (
      <div
        onClick={() => this.props.isDemo ? this.props.togglePlanDialog() : this.props.goTo('/order-dashboard' + status.path, { ownerType: this.props.ownerType, dateSelectedValue: this.state.dateSelected.value })}
        className={classes.link}
      >
        <div className={classes.infoContainer}>
          <div className={classes.statusText}>
            <span className={classes.circle} style={{ backgroundColor: status.text === 'クローズ' ? StatusChartPanel.CLOSE_COLOR : status.color }}></span>
            {status.text === '注文受付' ? DashboardTimelineType.APPROACH.text : status.text}
          </div>
          <div className={classes.infoText}>{info && info["count"] ? addCommas(info["count"]) : 0}人</div>
          <div className={classes.infoText} style={{ width: 45 }}>{info && info["percentage"] ? info["percentage"] : 0}%</div>
        </div>
      </div>
    )
  }

  handleSelectDate = (item) => {
    let filterValue = {
      created_at_from: item.from ? item.from : null,
      created_at_to: item.to ? item.to : null,
    };
    this.setState({
      dateSelected: item,
      filterValue: { ...this.state.filterValue, ...filterValue },
    }, () => {
      let query = this.filterValueToQuery();
      this.props.getCurrentPersonAllStatus({
        ...query
      }, true);
      this.props.getOrdersOverview({
        ...query
      }, true);
    });
  }

  filterValueToQuery = (filterValue = this.state.filterValue, ownerType = this.props.ownerType) => {
    let data = {};
    for (let property in filterValue) {
      if (property === 'created_at_from' || property === 'created_at_to') {
        data[property] = filterValue[property] ? filterValue[property].format('YYYY-MM-DD') : null;
      } else {
        data[property] = filterValue[property];
      }
    }

    data["is_own"] = ownerType === OwnerType.OWN.value ? 'true' : 'false';

    return data;
  }

  getOrderSummaryParam = (property) => {
    const { overview } = this.props;
    return overview && overview.hasOwnProperty(property) ? overview[property] : 0;
  }

  getSumStatusCount = () => {
    const { pageInfoCurrentPersonStatuses, isDemo } = this.props;

    let sum = 0;
    if (isDemo) {
      sum = Object.values(StatusChartPanel.DEMO_DATA["CURRENT_PERSON_STATUS"]).reduce((a, b) => a + b);
    } else {
      sum = Object.values(pageInfoCurrentPersonStatuses).reduce((a, b) => a + (b && b.total ? b.total : 0), 0);
    }

    return sum;
  }

  getChartData = () => {
    const { pageInfoCurrentPersonStatuses, isDemo } = this.props;

    const hasKLFollowCallBeenEnabledUser = hasKLFollowCallBeenEnabled(this.props.user);

    const approachCount = isDemo ? StatusChartPanel.DEMO_DATA["CURRENT_PERSON_STATUS"]["APPROACH_COUNT"] : pageInfoCurrentPersonStatuses[TimelineLabelType.APPROACH.value] ? pageInfoCurrentPersonStatuses[TimelineLabelType.APPROACH.value].total : 0;
    const nonConnectCount = isDemo ? StatusChartPanel.DEMO_DATA["CURRENT_PERSON_STATUS"]["NON_CONNECT_COUNT"] : pageInfoCurrentPersonStatuses[TimelineLabelType.NON_CONNECT.value] ? pageInfoCurrentPersonStatuses[TimelineLabelType.NON_CONNECT.value].total : 0;
    const declinedCount = isDemo ? StatusChartPanel.DEMO_DATA["CURRENT_PERSON_STATUS"]["DECLINED_COUNT"] : pageInfoCurrentPersonStatuses[TimelineLabelType.DECLINED.value] ? pageInfoCurrentPersonStatuses[TimelineLabelType.DECLINED.value].total : 0;
    const connectCount = isDemo ? StatusChartPanel.DEMO_DATA["CURRENT_PERSON_STATUS"]["CONNECT_COUNT"] : pageInfoCurrentPersonStatuses[TimelineLabelType.CONNECT.value] ? pageInfoCurrentPersonStatuses[TimelineLabelType.CONNECT.value].total : 0;
    const sentDocumentCount = isDemo ? StatusChartPanel.DEMO_DATA["CURRENT_PERSON_STATUS"]["SENT_DOCUMENT_COUNT"] : pageInfoCurrentPersonStatuses[TimelineLabelType.SENT_DOCUMENT.value] ? pageInfoCurrentPersonStatuses[TimelineLabelType.SENT_DOCUMENT.value].total : 0;
    const appointmentCount = isDemo ? StatusChartPanel.DEMO_DATA["CURRENT_PERSON_STATUS"]["APPOINTMENT_COUNT"] : pageInfoCurrentPersonStatuses[TimelineLabelType.APPOINTMENT.value] ? pageInfoCurrentPersonStatuses[TimelineLabelType.APPOINTMENT.value].total : 0;
    const approachNgCount = isDemo ? StatusChartPanel.DEMO_DATA["CURRENT_PERSON_STATUS"]["APPROACH_NG_COUNT"] : pageInfoCurrentPersonStatuses[TimelineLabelType.APPROACH_NG.value] ? pageInfoCurrentPersonStatuses[TimelineLabelType.APPROACH_NG.value].total : 0;
    const closeCount = isDemo ? StatusChartPanel.DEMO_DATA["CURRENT_PERSON_STATUS"]["CLOSE_COUNT"] : pageInfoCurrentPersonStatuses[TimelineLabelType.CLOSE.value] ? pageInfoCurrentPersonStatuses[TimelineLabelType.CLOSE.value].total : 0;

    let sum = approachCount + appointmentCount + approachNgCount + closeCount;
    if (hasKLFollowCallBeenEnabledUser || isDemo) {
      sum += (nonConnectCount + declinedCount + connectCount + sentDocumentCount);
    }

    const approachPer = this.calculatePercentage(approachCount, sum);
    const nonConnectPer = this.calculatePercentage(nonConnectCount, sum);
    const declinedPer = this.calculatePercentage(declinedCount, sum);
    const connectPer = this.calculatePercentage(connectCount, sum);
    const sentDocumentPer = this.calculatePercentage(sentDocumentCount, sum);
    const appointmentPer = this.calculatePercentage(appointmentCount, sum);
    const approachNgPer = this.calculatePercentage(approachNgCount, sum);
    const closePer = this.calculatePercentage(closeCount, sum);

    const followCallStatusCountParams = (hasKLFollowCallBeenEnabledUser || isDemo) ? [
      nonConnectCount,
      declinedCount,
      connectCount,
      sentDocumentCount,
    ] : [];

    const followCallPerParams = (hasKLFollowCallBeenEnabledUser || isDemo) ? [
      nonConnectPer,
      declinedPer,
      connectPer,
      sentDocumentPer,
    ] : [];

    const followCallColorParams = (hasKLFollowCallBeenEnabledUser || isDemo) ? [
      TimelineLabelType.NON_CONNECT.color,
      TimelineLabelType.DECLINED.color,
      TimelineLabelType.CONNECT.color,
      TimelineLabelType.SENT_DOCUMENT.color,
    ] : [];

    const followCallBorderParams = (hasKLFollowCallBeenEnabledUser || isDemo) ? [
      0,
      0,
      0,
      0,
    ] : [];

    return {
      labels: [
        approachCount,
        ...followCallStatusCountParams,
        appointmentCount,
        approachNgCount,
        closeCount,
      ],
      datasets: [{
        data: [
          approachPer,
          ...followCallPerParams,
          appointmentPer,
          approachNgPer,
          closePer,
        ],
        backgroundColor: [
          TimelineLabelType.APPROACH.color,
          ...followCallColorParams,
          TimelineLabelType.APPOINTMENT.color,
          TimelineLabelType.APPROACH_NG.color,
          StatusChartPanel.CLOSE_COLOR,
        ],
        borderWidth: [
          0,
          ...followCallBorderParams,
          0,
          0,
          0,
        ],
      }],
      info: {
        approach: { count: approachCount, percentage: approachPer },
        non_connect: { count: nonConnectCount, percentage: nonConnectPer },
        declined: { count: declinedCount, percentage: declinedPer },
        connect: { count: connectCount, percentage: connectPer },
        sent_document: { count: sentDocumentCount, percentage: sentDocumentPer },
        appointment: { count: appointmentCount, percentage: appointmentPer },
        approach_ng: { count: approachNgCount, percentage: approachNgPer },
        close: { count: closeCount, percentage: closePer },
      },
    };
  }

  getOptions = () => {
    return {
      maintainAspectRatio: false,
      responsive: false,
      cutoutPercentage: 60,
      legend: {
        display: false,
      },
      tooltips: { enabled: false },
      plugins: {
        doughnutlabel: {
          labels: [
            {
              text: addCommas(this.getSumStatusCount()) + '人',
              color: '#000000',
              font: {
                size: 20,
              },
            },
          ],
        },
        datalabels: {
          display: 'auto',
          clip: true,
          color: '#FFFFFF',
          textAlign: 'center',
          padding: {
            top: 0,
            bottom: 0,
            left: 5,
            right: 5,
          },
          font: {
            size: 11,
          },
          formatter: (value, ctx) => {
            const label = ctx.chart.data.labels[ctx.dataIndex];
            return value > 0 ? `${addCommas(label)}人\n(${value}%)` : '';
          },
        }
      },
    };
  }

  render() {
    const { classes, isDemo } = this.props;

    return (
      <div className={classes.root}>
        <PanelHeader
          title={"最新のステータス（人別）"}
          type={null}
          dateRanges={DateRanges}
          dateSelected={this.state.dateSelected}
          handleSelectDate={this.handleSelectDate}
          togglePlanDialog={this.props.togglePlanDialog}
        />
        {(() => {
          // ローディング中
          if (!isDemo && this.props.isLoading) {
            return (
              <div className={classes.loaderContainer}>
                <Loader />
              </div>
            );
          }

          // ローディング完了したが、項目がない
          if (!isDemo && this.getSumStatusCount() === 0 && !this.props.isLoading) {
            return (
              <div className={classes.emptyContainer}>
                アイテムがありません。
              </div>
            );
          }

          // ローディング完了またはデモの場合
          const chartData = this.getChartData();
          const hasKLFollowCallBeenEnabledUser = hasKLFollowCallBeenEnabled(this.props.user);
          return (
            <div className={classes.root}>
              <div className={classes.graphContainer}>
                <div style={{ cursor: 'pointer' }}
                  onClick={() => isDemo ? this.props.togglePlanDialog() : this.props.goTo('/order-dashboard' + DashboardTimelineType.BY_FOLLOW_CALL_STATUS.path, { ownerType: this.props.ownerType, dateSelectedValue: this.state.dateSelected.value })}
                >
                  <Doughnut
                    data={chartData}
                    options={this.getOptions()}
                    width={250}
                    height={250}
                  />
                </div>
                <div className={classes.graphInfo}>
                  {this.renderLink(TimelineLabelType.APPROACH, chartData.info.approach)}
                  {(hasKLFollowCallBeenEnabledUser || isDemo) && (
                    <React.Fragment>
                      {this.renderLink(TimelineLabelType.NON_CONNECT, chartData.info.non_connect)}
                      {this.renderLink(TimelineLabelType.DECLINED, chartData.info.declined)}
                      {this.renderLink(TimelineLabelType.CONNECT, chartData.info.connect)}
                      {this.renderLink(TimelineLabelType.SENT_DOCUMENT, chartData.info.sent_document)}
                    </React.Fragment>
                  )}
                  {this.renderLink(TimelineLabelType.APPOINTMENT, chartData.info.appointment)}
                  {this.renderLink(TimelineLabelType.APPROACH_NG, chartData.info.approach_ng)}
                  {this.renderLink(TimelineLabelType.CLOSE, chartData.info.close)}
                </div>
              </div>
              <div className={classes.info}>
                <div style={{ textAlign: "left" }}>
                  {this.state.dateSelected.text}に
                  <br />
                  <span>
                    計{isDemo ? StatusChartPanel.DEMO_DATA["LETTER_ORDER"]["ORDER_COUNT"] : this.getOrderSummaryParam("letter_order_count")}回・
                    全{isDemo ? StatusChartPanel.DEMO_DATA["LETTER_ORDER"]["ADDRESS_COUNT"] : this.getOrderSummaryParam("letter_order_address_count")}通のレター注文を行いました
                  </span>
                  <br />
                  {(hasKLFollowCallBeenEnabledUser || isDemo) && (
                    <span>
                      計{isDemo ? StatusChartPanel.DEMO_DATA["FOLLOW_CALL_ORDER"]["ORDER_COUNT"] : this.getOrderSummaryParam("follow_call_order_count")}回・
                      全{isDemo ? StatusChartPanel.DEMO_DATA["FOLLOW_CALL_ORDER"]["ADDRESS_COUNT"] : this.getOrderSummaryParam("follow_call_order_address_count")}人のフォローコール注文を行いました
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  }
}

StatusChartPanel.propTypes = {
  classes: PropTypes.object.isRequired,
  user: PropTypes.object,
  isDemo: PropTypes.bool,
  goTo: PropTypes.func.isRequired,
  ownerType: PropTypes.string.isRequired,
  // ステータスとレター注文サマリーを合わせてロード中とする
  isLoading: PropTypes.bool.isRequired,
  // ステータス
  getCurrentPersonAllStatus: PropTypes.func.isRequired,
  pageInfoCurrentPersonStatuses: PropTypes.object.isRequired,
  // レター注文サマリー
  getOrdersOverview: PropTypes.func.isRequired,
  overview: PropTypes.object.isRequired,
  togglePlanDialog: PropTypes.func.isRequired,
};

StatusChartPanel.CLOSE_COLOR = '#999999';  // ダッシュボードではクローズだけこの色を表示する（見づらいため）

StatusChartPanel.DEMO_DATA = {
  "CURRENT_PERSON_STATUS": {
    "APPROACH_COUNT": 29,
    "NON_CONNECT_COUNT": 3,
    "DECLINED_COUNT": 4,
    "CONNECT_COUNT": 7,
    "SENT_DOCUMENT_COUNT": 3,
    "APPOINTMENT_COUNT": 4,
    "APPROACH_NG_COUNT": 1,
    "CLOSE_COUNT": 2,
  },
  "LETTER_ORDER": {
    "ORDER_COUNT": 20,
    "ADDRESS_COUNT": 53,
  },
  "FOLLOW_CALL_ORDER": {
    "ORDER_COUNT": 5,
    "ADDRESS_COUNT": 17,
  },
};

const mapStateToProps = (state) => {
  return {
    user: state.accountSetting.user,
    isDemo: state.accountSetting.showDemoData,
    overview: state.orderList.overview,
    isLoading: state.orderList.isLoadingOverview || (typeof state.currentPersonStatus.isLoadingCurrentPersonStatuses === "object" && Object.keys(state.currentPersonStatus.isLoadingCurrentPersonStatuses).length > 0) ? Object.values(state.currentPersonStatus.isLoadingCurrentPersonStatuses).some(item => item === true) : true,
    pageInfoCurrentPersonStatuses: state.currentPersonStatus.pageInfoCurrentPersonStatuses,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getCurrentPersonAllStatus(data) {
      dispatch(getCurrentPersonAllStatus(data))
    },
    getOrdersOverview(data) {
      dispatch(actions.getOrdersOverview(
        {
          ...data,
          targets: [
            "letter_order_count",
            "letter_order_address_count",
            "follow_call_order_count",
            "follow_call_order_address_count",
          ]
        }
      ));
    },
  };
};


export default withStyles(styles)(connect(
  mapStateToProps,
  mapDispatchToProps
)(StatusChartPanel));



// WEBPACK FOOTER //
// ./src/components/OrderDashboard/panels/StatusChartPanel.jsx